import React, { useMemo } from 'react';
import { EQ } from './TriGlyph';
import './Triangles.css';

/** The field's box per edge, in user units; the SVG slices it to the surface, kept to the edge. */
const BOX = {
  bottom: { w: 800, h: 160 },
  right: { w: 240, h: 200 },
} as const;
const CELL = 16;

export interface FieldTri { x: number; y: number; h: number }

/**
 * The rising field's triangles: one fine grain on a grid, all pointing up and standing on their
 * cell's floor. They fill the wedge under the diagonal from the bottom-left corner to the
 * top-right one, and grow along it, small to big: one direction, one progression.
 */
export function fieldTriangles(w: number, h: number): FieldTri[] {
  const out: FieldTri[] = [];
  const cols = Math.floor(w / CELL);
  const rows = Math.floor(h / CELL);
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const u = (c + 0.5) / cols;      // 0 at the left, 1 at the right
      const v = (r + 0.5) / rows;      // 0 at the bottom, 1 at the top
      if (v > u) continue;             // above the rising diagonal: empty
      const t = 0.7 * u + 0.3 * v;     // bottom-left to top-right
      const th = CELL * (0.2 + 0.6 * t);
      out.push({ x: c * CELL + CELL / 2, y: h - r * CELL - CELL * 0.1, h: th });
    }
  }
  return out;
}

/**
 * Grow's one texture (DESIGN_SYSTEM.md "Triangles in Grow"): a rising field at an edge of its
 * surface, about a fifth of it, low contrast. The parent must be `position: relative` with
 * `overflow: hidden`. tone "field" is Cool Grey Light for Creme; "brand" is 12% white for purple.
 */
export function RisingField({ edge = 'bottom', tone = 'field', className }: {
  edge?: 'bottom' | 'right'; tone?: 'field' | 'brand'; className?: string;
}) {
  const { w, h } = BOX[edge];
  const tris = useMemo(() => fieldTriangles(w, h), [w, h]);
  const d = tris.map((t) => {
    const half = t.h / EQ / 2;
    return `M${(t.x - half).toFixed(1)} ${t.y.toFixed(1)}L${(t.x + half).toFixed(1)} ${t.y.toFixed(1)}L${t.x.toFixed(1)} ${(t.y - t.h).toFixed(1)}Z`;
  }).join('');
  return (
    <svg
      className={['ui-field', `ui-field--${edge}`, `ui-field--${tone}`, className].filter(Boolean).join(' ')}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMaxYMax slice"
      aria-hidden="true"
      focusable="false"
      data-testid="rising-field"
    >
      <path d={d} />
    </svg>
  );
}
